import { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import type { Personne, Pole } from '../types';
import { PoleBadge, PoleChips } from '../components/annuaire/PoleChips';
import { PersonneModal } from '../components/annuaire/PersonneModal';
import { echeanceClass, formatDate, LIBELLES_STATUT_EXTERNE, statutExterneBadgeClass } from '../components/annuaire/format';

type Onglet = 'internes' | 'externe';

// Annuaire des intervenant·e·s internes + vivier externe. Les personnes
// viennent d'Eveil V2.1 (identité et « actif » en lecture seule) ; le portail
// gère le cycle de vie du vivier, les pôles déclarés et les notes. La
// prochaine_revue est calculée côté serveur (réglage vivier.revue_mois).
export function RemplacantsPage() {
  const { peutGerer } = useRole();
  const [onglet, setOnglet] = useState<Onglet>('internes');
  const [poles, setPoles] = useState<Pole[]>([]);
  const [personnes, setPersonnes] = useState<Personne[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [poleId, setPoleId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Personne | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [meta, res] = await Promise.all([
        api.getMeta(),
        // Internes : personnel en poste (contrat en cours ou créneau animé, selon Eveil).
        api.getPersonnes({ externe: onglet === 'externe', actif: onglet === 'internes' ? true : undefined, q: q || undefined, pole_id: poleId ?? undefined }),
      ]);
      setPoles(meta.poles);
      setPersonnes(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec du chargement');
    } finally {
      setLoading(false);
    }
  }, [onglet, q, poleId]);

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
  }, [fetchAll]);

  const majPersonne = async (id: number, data: Partial<Personne>) => {
    try {
      const updated = await api.updatePersonne(id, data);
      setPersonnes((prev) => prev.map((p) => (p.id === id ? updated : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la mise à jour');
    }
  };

  const ouvrirCreation = () => { setEditing(null); setModalOpen(true); };
  const ouvrirEdition = (p: Personne) => { setEditing(p); setModalOpen(true); };
  const fermerModal = () => { setModalOpen(false); setEditing(null); };
  const surEnregistrement = () => { fermerModal(); fetchAll(); };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading"><span className="page-title">Remplaçants</span></div>
      </div>

      {/* Le vivier externe est l'affaire de la gestion : un seul onglet (donc
          pas de barre d'onglets) pour l'intervenant·e. */}
      {peutGerer && (
        <div className="tabs">
          <div className={`tab ${onglet === 'internes' ? 'active' : ''}`} onClick={() => setOnglet('internes')}>Internes</div>
          <div className={`tab ${onglet === 'externe' ? 'active' : ''}`} onClick={() => setOnglet('externe')}>Vivier externe</div>
        </div>
      )}

      <div className="toolbar">
        <PoleChips poles={poles} selected={poleId} onSelect={setPoleId} />
        <div className="search-wrapper">
          <input className="search-input" placeholder="Rechercher un nom…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {peutGerer && onglet === 'externe' && (
          <button className="btn btn-accent" onClick={ouvrirCreation}><Plus className="icon-sm" /> Ajouter un·e candidat·e</button>
        )}
      </div>

      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}

      {loading ? (
        <div className="spinner" />
      ) : personnes.length === 0 ? (
        <div className="empty-state">Aucune personne trouvée.</div>
      ) : onglet === 'internes' ? (
        <table className="table">
          <thead><tr><th>Nom</th><th>Pôles</th><th>E-mail</th><th>Téléphone</th><th>Tipee</th><th></th></tr></thead>
          <tbody>
            {personnes.map((p) => (
              <tr key={p.id}>
                <td className="font-medium">{p.display_name}</td>
                <td>
                  <div className="flex flex-wrap gap-8">
                    {(p.pole_ids ?? []).map((pid) => {
                      const pole = poles.find((x) => x.id === pid);
                      return pole ? <PoleBadge key={pid} nom={pole.nom} couleur={pole.couleur} /> : null;
                    })}
                  </div>
                </td>
                <td className="text-sm">{p.email ?? '—'}</td>
                <td className="text-sm">{p.telephone ?? '—'}</td>
                <td className="text-sm" title={p.tipee_resource_id ? 'Relié·e à Tipee' : 'Pas encore relié·e à Tipee : ses absences ne sont pas détectées'}>
                  {p.tipee_resource_id ? 'Relié·e' : <span className="text-warn">À relier</span>}
                </td>
                <td>
                  {peutGerer && (
                    <button className="btn btn-icon" title="Modifier" onClick={() => ouvrirEdition(p)}><Pencil className="icon-sm" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="table">
          <thead><tr><th>Nom</th><th>Statut</th><th>Prochaine revue</th><th>Téléphone</th><th>E-mail</th><th>Actions</th></tr></thead>
          <tbody>
            {personnes.map((p) => (
              <tr key={p.id}>
                <td className="font-medium">
                  {p.display_name}
                  {p.origine === 'candidat' && <div className="text-xs text-ink-3">Pas encore dans Eveil</div>}
                </td>
                <td><span className={statutExterneBadgeClass(p.statut_externe)}>{p.statut_externe ? LIBELLES_STATUT_EXTERNE[p.statut_externe] : '—'}</span></td>
                <td className={echeanceClass(p.prochaine_revue)}>{formatDate(p.prochaine_revue)}</td>
                <td className="text-sm">{p.telephone ?? '—'}</td>
                <td className="text-sm">{p.email ?? '—'}</td>
                <td>
                  <div className="flex gap-8 flex-wrap">
                    {peutGerer && p.statut_externe === 'candidat' && (
                      <button className="btn btn-sm" onClick={() => majPersonne(p.id, { statut_externe: 'visite_planifiee' })}>Planifier visite</button>
                    )}
                    {peutGerer && p.statut_externe === 'visite_planifiee' && (
                      <>
                        <button className="btn btn-sm btn-accent" onClick={() => majPersonne(p.id, { statut_externe: 'actif' })}>Valider (visite faite)</button>
                        <button className="btn btn-sm btn-danger" onClick={() => majPersonne(p.id, { statut_externe: 'archive' })}>Refuser</button>
                      </>
                    )}
                    {peutGerer && p.statut_externe === 'actif' && (
                      <>
                        <button className="btn btn-sm" onClick={() => majPersonne(p.id, { prochaine_revue: null })}>Revue faite</button>
                        <button className="btn btn-sm btn-danger" onClick={() => majPersonne(p.id, { statut_externe: 'inactif' })}>Désactiver</button>
                      </>
                    )}
                    {peutGerer && p.statut_externe === 'inactif' && (
                      <>
                        <button className="btn btn-sm btn-accent" onClick={() => majPersonne(p.id, { statut_externe: 'actif' })}>Réactiver</button>
                        <button className="btn btn-sm btn-danger" onClick={() => majPersonne(p.id, { statut_externe: 'archive' })}>Archiver</button>
                      </>
                    )}
                    {peutGerer && (
                      <button className="btn btn-icon" title="Modifier" onClick={() => ouvrirEdition(p)}><Pencil className="icon-sm" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <PersonneModal poles={poles} personne={editing} defaultExterne={onglet === 'externe'} onClose={fermerModal} onSaved={surEnregistrement} />
      )}
    </div>
  );
}
